import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Truck, MapPin, Phone, Check, Navigation,
  Clock, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import { formatDateWithDay } from '../utils/dateUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../lib/authToken';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Statuts chauffeur ordonnés
const STATUTS_CHAUFFEUR = [
  { key: 'pret_depart',     label: 'Prêt à partir',    color: '#3B82F6', emoji: '🔵' },
  { key: 'en_route',        label: 'En route',          color: '#8B5CF6', emoji: '🟣' },
  { key: 'arrive',          label: 'Arrivé sur place',  color: '#F59E0B', emoji: '🟡' },
  { key: 'installe',        label: 'Installé ✓',        color: '#22C55E', emoji: '✅' },
  { key: 'retour_prevu',    label: 'Retour prévu',      color: '#F97316', emoji: '🟠' },
  { key: 'retour_recupere', label: 'Matériel récupéré', color: '#D4AF37', emoji: '🔄' },
];

function FicheLivreur({ fiche, onStatutChange, onToggleCheck }) {
  const [open, setOpen] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const statutConf = STATUTS_CHAUFFEUR.find(s => s.key === fiche.statut) || STATUTS_CHAUFFEUR[0];
  const currentIdx = STATUTS_CHAUFFEUR.findIndex(s => s.key === fiche.statut);
  const nextStatut = STATUTS_CHAUFFEUR[currentIdx + 1];

  const checkDone = (fiche.checklist || []).filter(c => c.done).length;
  const checkTotal = (fiche.checklist || []).length;

  return (
    <div className="rounded-2xl overflow-hidden mb-3"
      style={{ border: `2px solid ${statutConf.color}`, background: '#FFFFFF' }}>

      {/* Header */}
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left"
        style={{ cursor: 'pointer', background: `${statutConf.color}08`, border: 'none' }}>
        <span className="text-2xl">{statutConf.emoji}</span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm" style={{ color: '#0B1E20' }}>{fiche.client_nom}</p>
          <p className="text-xs" style={{ color: 'rgba(11,30,32,0.6)' }}>
            <Clock size={10} className="inline mr-1" />{fiche.heure_livraison || '?h?'}
            {fiche.adresse && ` · ${fiche.adresse.split(',')[0]}`}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-xs font-bold px-2 py-1 rounded-full"
            style={{ background: statutConf.color, color: '#FFF' }}>
            {statutConf.label}
          </span>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(11,30,32,0.5)' }}>
            ✓ {checkDone}/{checkTotal}
          </p>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          {/* Infos client */}
          <div className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(11,30,32,0.04)' }}>
            {fiche.client_telephone && (
              <a href={`tel:${fiche.client_telephone}`}
                className="flex items-center gap-2 text-sm font-semibold"
                style={{ color: '#5DBCBF' }}>
                <Phone size={15} /> {fiche.client_telephone}
              </a>
            )}
            {fiche.adresse && (
              <p className="flex items-start gap-2 text-xs" style={{ color: '#0B1E20' }}>
                <MapPin size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                {fiche.adresse}
              </p>
            )}
            {fiche.acces && (
              <p className="flex items-start gap-2 text-xs" style={{ color: '#F97316' }}>
                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                Accès : {fiche.acces}
              </p>
            )}
            {fiche.notes_client && (
              <p className="text-xs italic" style={{ color: 'rgba(11,30,32,0.5)' }}>
                💬 "{fiche.notes_client}"
              </p>
            )}
          </div>

          {/* Matériel */}
          <div>
            <p className="text-xs font-bold mb-1.5" style={{ color: '#0B1E20' }}>
              📦 Matériel ({fiche.items?.length || 0} articles)
            </p>
            <div className="space-y-1">
              {(fiche.items || []).map((item, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1.5 rounded-lg"
                  style={{ background: 'rgba(11,30,32,0.04)' }}>
                  <span className="text-xs" style={{ color: '#0B1E20' }}>{item.product_nom}</span>
                  <span className="text-xs font-bold" style={{ color: '#5DBCBF' }}>×{item.quantite || 1}</span>
                </div>
              ))}
            </div>
          </div>

          {/* GPS */}
          {fiche.adresse && (
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fiche.adresse)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold"
              style={{ background: '#0B1E20', color: '#D4AF37', textDecoration: 'none' }}>
              <Navigation size={15} /> Ouvrir la navigation GPS
            </a>
          )}

          {/* Checklist */}
          <button onClick={() => setShowChecklist(!showChecklist)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold"
            style={{ background: 'rgba(93,188,191,0.08)', border: '1px solid rgba(93,188,191,0.2)', color: '#0B4B4E', cursor: 'pointer' }}>
            <span>✅ Checklist — {checkDone}/{checkTotal} points</span>
            {showChecklist ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {showChecklist && (
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {(fiche.checklist || []).map(item => (
                <button key={item.id}
                  onClick={() => onToggleCheck(fiche.id, item.id, !item.done)}
                  className="w-full flex items-start gap-2.5 p-2.5 rounded-xl text-left"
                  style={{ background: item.done ? 'rgba(34,197,94,0.08)' : 'var(--bg)', border: 'none', cursor: 'pointer' }}>
                  <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: item.done ? '#22C55E' : '#D1D5DB', background: item.done ? '#22C55E' : 'transparent' }}>
                    {item.done && <Check size={10} color="white" />}
                  </div>
                  <span className="text-xs" style={{ color: item.done ? '#9CA3AF' : '#0B1E20', textDecoration: item.done ? 'line-through' : 'none' }}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Avancer statut */}
          <div className="space-y-2">
            {nextStatut && (
              <button
                onClick={() => onStatutChange(fiche.id, nextStatut.key)}
                className="w-full py-3.5 rounded-xl font-bold text-sm"
                style={{ background: nextStatut.color, color: '#FFFFFF', border: 'none', cursor: 'pointer' }}>
                {nextStatut.emoji} Passer à : {nextStatut.label}
              </button>
            )}

            {/* Tous les statuts */}
            <div className="flex flex-wrap gap-1.5">
              {STATUTS_CHAUFFEUR.map(s => (
                <button key={s.key}
                  onClick={() => onStatutChange(fiche.id, s.key)}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold"
                  style={{
                    background: fiche.statut === s.key ? s.color : `${s.color}15`,
                    color: fiche.statut === s.key ? '#FFF' : s.color,
                    border: `1px solid ${s.color}40`,
                    cursor: 'pointer',
                  }}>
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LivreurApp() {
  const { user } = useAuth();
  // ANO-013 : l'app terrain s'appuie sur le compte connecté (ose_auth) + rôle habilité (admin ou livreur).
  const isAuthorized = !!user && (user.role === 'admin' || user.role === 'livreur');
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  // Le nom de chauffeur vient du compte : un livreur ne voit que ses fiches, l'admin voit toute la tournée.
  const chauffeurNom = user?.role === 'livreur' ? (user?.prenom || '') : '';
  // Nom affiché dans l'en-tête (jamais vide) — distinct du filtre de tournée.
  const displayName = user?.prenom || user?.nom || 'chauffeur';
  const [today] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    if (!isAuthorized) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = chauffeurNom ? `chauffeur=${encodeURIComponent(chauffeurNom)}&` : '';
      const res = await fetch(`${BACKEND}/api/chauffeur/tournee?${qs}date_str=${today}`, { headers: authHeaders() });
      if (res.status === 401 || res.status === 403) {
        toast.error('Session expirée — reconnectez-vous');
        setFiches([]); setLoading(false); return;
      }
      if (res.ok) {
        const data = await res.json();
        setFiches(data.fiches || []);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error(e);
    }
    setLoading(false);
  }, [isAuthorized, chauffeurNom, today]);

  useEffect(() => { load(); }, [load]);

  const handleStatutChange = async (ficheId, statut) => {
    try {
      await fetch(`${BACKEND}/api/chauffeur/fiche/${ficheId}/statut`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ statut }),
      });
      setFiches(prev => prev.map(f => f.id === ficheId ? { ...f, statut } : f));
      toast.success(`✓ ${STATUTS_CHAUFFEUR.find(s => s.key === statut)?.label || statut}`);
    } catch {
      toast.error('Erreur mise à jour');
    }
  };

  const handleToggleCheck = async (ficheId, itemId, done) => {
    try {
      await fetch(`${BACKEND}/api/chauffeur/fiche/${ficheId}/checklist/${itemId}?done=${done}`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      setFiches(prev => prev.map(f => {
        if (f.id !== ficheId) return f;
        return { ...f, checklist: (f.checklist || []).map(c => c.id === itemId ? { ...c, done } : c) };
      }));
    } catch { }
  };

  // ANO-013 : accès réservé aux comptes habilités — plus de saisie de prénom libre.
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6"
        style={{ background: '#0B1E20' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: 'rgba(212,175,55,0.2)' }}>
          <Truck size={32} style={{ color: '#D4AF37' }} />
        </div>
        <h1 className="font-display text-2xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
          Espace Chauffeur
        </h1>
        <p className="text-sm mb-8 text-center" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {user ? "Votre compte n'a pas accès à l'espace terrain." : 'Connectez-vous pour accéder à votre tournée.'}
        </p>
        <Link to="/connexion?redirect=/livreur" data-testid="livreur-login-link"
          className="w-full max-w-xs py-3.5 rounded-xl font-bold text-sm text-center"
          style={{ background: '#D4AF37', color: '#0B1E20', textDecoration: 'none' }}>
          Se connecter →
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1E20' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3" style={{ background: '#0B1E20', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck size={18} style={{ color: '#D4AF37' }} />
            <div>
              <p className="font-bold text-sm" style={{ color: '#FFFFFF' }}>Bonjour {displayName} 👋</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {formatDateWithDay(new Date())}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1.5 rounded-full"
              style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
              {fiches.length} livraison{fiches.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        {loading ? (
          <div className="space-y-3 mt-4">
            {[1, 2].map(i => (
              <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            ))}
          </div>
        ) : fiches.length === 0 ? (
          <div className="text-center py-16">
            <Check size={40} style={{ color: '#22C55E', margin: '0 auto 12px' }} />
            <p className="font-semibold text-sm mb-1" style={{ color: '#FFFFFF' }}>
              Aucune livraison aujourd'hui
            </p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Votre tournée est vide pour le {today}
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {fiches.map(fiche => (
              <FicheLivreur
                key={fiche.id}
                fiche={fiche}
                onStatutChange={handleStatutChange}
                onToggleCheck={handleToggleCheck}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
